import React from "react";

import { Entry } from "../models/entries";
import { UserJSON } from "../models/users";
import { FormattedText } from "./FormattedText";
import { Tooltip } from "./Tooltip";
import { RLink } from "./RLink";

interface EntryDescriptionProps {
  entry: Entry;
  secretSantaRecipient?: UserJSON;
  secretSantaRecipientExternalUserName?: string;
}

const Badge = ({
  children,
  tooltip,
}: {
  children: React.ReactNode;
  tooltip?: string;
}) => {
  const badge = (
    <span className="inline-flex cursor-default items-center rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-white/70">
      {children}
    </span>
  );

  if (tooltip) {
    return <Tooltip element={badge} tooltip={tooltip} position="top" />;
  }

  return badge;
};

export function EntryDescription({
  entry,
  secretSantaRecipient,
  secretSantaRecipientExternalUserName,
}: EntryDescriptionProps): JSX.Element {
  const badges: React.ReactNode[] = [];

  if (entry.followed_theme === false) {
    badges.push(
      <Badge
        key="off-theme"
        tooltip="This author indicated that this entry did not follow the theme."
      >
        Off Theme
      </Badge>
    );
  }

  if (entry.is_joke) {
    badges.push(
      <Badge
        key="joke"
        tooltip="The author submitted this as a joke enty. It will not receive votes."
      >
        Joke Entry
      </Badge>
    );
  } else if (!entry.is_judged) {
    badges.push(
      <Badge key="no-vote" tooltip="This entry will not be voted on.">
        Not In Voting
      </Badge>
    );
  }

  return (
    <section className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white/90 px-6 py-5 text-base leading-relaxed text-slate-800 shadow-sm backdrop-blur-sm dark:border-slate-700 dark:bg-slate-800 dark:shadow-none dark:text-slate-300">
        <h3 className="mb-4 text-2xl font-semibold text-slate-800 dark:text-white">
          Author's Description
        </h3>
        <div>
          <FormattedText text={entry.description || ""} />
        </div>
        {badges.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">{badges}</div>
        )}
      </div>

      {(secretSantaRecipient || secretSantaRecipientExternalUserName) && (
        <div className="rounded-2xl border border-slate-200 bg-white/90 px-6 py-5 shadow-sm backdrop-blur-sm dark:border-slate-700 dark:bg-slate-800 dark:shadow-none">
          <h3 className="mb-4 flex items-center gap-2 text-2xl font-semibold text-slate-800 dark:text-white">
            <span className="text-3xl">🎅</span> Secret Santa
          </h3>
          {secretSantaRecipient ? (
            <div className="flex items-center gap-3 text-base text-slate-800 dark:text-slate-300">
              <span>This is a gift for</span>
              <RLink
                to={`/user/${secretSantaRecipient.username}`}
                className="flex items-center gap-2 font-medium text-chips-gold hover:underline"
              >
                <img
                  src={
                    secretSantaRecipient.avatar_url || "/img/default-chip.png"
                  }
                  alt={secretSantaRecipient.username}
                  className="h-8 w-8 rounded-full"
                />
                {secretSantaRecipient.username}
              </RLink>
            </div>
          ) : (
            <div className="text-base text-slate-800 dark:text-slate-300">
              This is a gift for{" "}
              <span className="font-medium text-chips-gold">
                {secretSantaRecipientExternalUserName}
              </span>
            </div>
          )}
          {entry.secret_santa_prompt_text && (
            <div className="mt-4">
              <div className="mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                Secret Santa request:
              </div>
              <div className="rounded-lg bg-slate-100 p-3 text-base text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                <FormattedText text={entry.secret_santa_prompt_text} />
              </div>
            </div>
          )}
        </div>
      )}

      {entry.mod_comments && (
        <div className="rounded-2xl border border-slate-200 bg-white/90 px-6 py-5 shadow-sm backdrop-blur-sm dark:border-slate-700 dark:bg-slate-800 dark:shadow-none">
          <h3 className="mb-4 text-2xl font-semibold text-slate-800 dark:text-white">
            Moderator Notes
          </h3>
          <div className="text-base leading-relaxed text-slate-800 dark:text-slate-300">
            <FormattedText text={entry.mod_comments || ""} />
          </div>
        </div>
      )}
    </section>
  );
}
